import Image from 'next/image'

import { cn } from '@/lib/utils'

interface SmartImageProps {
    src: string | null | undefined
    alt: string | null | undefined
    sizes?: string
    preload?: boolean
    className?: string
}

// Images from the site's own storage (or the public folder) are resized and served as modern formats.
const isOptimizable = (src: string): boolean => src.startsWith('/') || /^https:\/\/[^/]+\.cdn\.modulify\.website\//.test(src)

/**
 * Fills its parent (which must be positioned and sized). Falls back to a soft gradient when no image is set,
 * and to a plain image tag for external addresses the optimiser does not know about.
 */
export const SmartImage = ({ src, alt, sizes = '100vw', preload = false, className }: SmartImageProps) => {
    if (!src) {
        return <div aria-hidden='true' className={cn('absolute inset-0 bg-gradient-to-br from-cream-200 via-gold-100 to-cream-300', className)} />
    }

    if (isOptimizable(src)) {
        return (
            <Image
                src={src}
                alt={alt ?? ''}
                fill
                sizes={sizes}
                preload={preload}
                className={cn('object-cover', className)}
            />
        )
    }

    return (
        <img
            src={src}
            alt={alt ?? ''}
            loading={preload ? 'eager' : 'lazy'}
            decoding='async'
            className={cn('absolute inset-0 h-full w-full object-cover', className)}
        />
    )
}
